import React, { useState } from "react"
import { Badge, Button, DatePicker, Popconfirm, message } from "antd"
import { updateDoc, doc, getDoc, serverTimestamp } from "firebase/firestore"
import { db } from "../../../../firebase/config"
import dayjs from "dayjs"

export default function HandleOrderPayment({ record, collectionName = "invoices" }) {
  // Local state to hold the chosen payment date; default to today.
  const [selectedDate, setSelectedDate] = useState(dayjs())

  // Determine status from the record. Expected status codes:
  let status
  if (collectionName === "invoices") {
    status = record.invoicePaymentStatus
  } else {
    status = record.proformaPaymentStatus
  }

  // Update function which uses the chosen date from the DatePicker.
  const handleMarkPaid = async () => {
    try {
      let updateFields
      if (collectionName === "invoices") {
        updateFields = {
          invoicePaymentStatus: 2,
          paidDateInvoiceTimestamp: serverTimestamp(),
          paidDateInvoice: selectedDate.toDate(),
        }
      } else if (collectionName === "proformas") {
        updateFields = {
          proformaPaymentStatus: 2,
          paidDateProformaTimestamp: serverTimestamp(),
          paidDateProforma: selectedDate.toDate(),
        }
      } else {
        message.error("Invalid collection name")
        return
      }

      // Update the order document in the "orders" collection.
      const orderRef = doc(db, "orders", record.id)
      await updateDoc(orderRef, updateFields)

      // Define update fields for the invoice/proforma document.
      let orderUpdateFields
      if (collectionName === "invoices") {
        orderUpdateFields = {
          paymentStatus: 2,
          paidDateInvoiceTimestamp: serverTimestamp(),
          paidDateInvoice: selectedDate.toDate(),
        }
      } else if (collectionName === "proformas") {
        orderUpdateFields = {
          paymentStatus: 2,
          paidDateProformaTimestamp: serverTimestamp(),
          paidDateProforma: selectedDate.toDate(),
        }
      }

      let docId
      if (collectionName === "invoices") {
        docId = record.invoice.invoiceId
      } else {
        docId = record.proforma.proformaId
      }

      const documentRef = doc(db, collectionName, docId)
      await updateDoc(documentRef, orderUpdateFields)

      // --- New logic: Update client credit ---
      const clientDocRef = doc(db, "clients", record.clientId)
      const clientSnap = await getDoc(clientDocRef)
      if (clientSnap.exists()) {
        const selectedClient = clientSnap.data()
        // Safely convert credit values; if conversion fails, default to 0.
        const currentCreditUsed = parseFloat(selectedClient.creditUsed) || 0
        const currentCreditLeft = parseFloat(selectedClient.creditLeft) || 0

        // Safely convert order values.
        const priceValue = parseFloat(record.price) || 0
        const rollCountValue = parseFloat(record.rollCount) || 0
        const quantityValue = parseFloat(record.labelCount) || 0

        // Calculate orderCost based on the machine type.
        let orderCost = 0
        if (record.selectedProduct.machine === "Tape") {
          orderCost = priceValue * rollCountValue
        } else {
          orderCost = priceValue * (quantityValue / 1000)
        }

        // Update client's credit values.
        await updateDoc(clientDocRef, {
          creditUsed: currentCreditUsed - orderCost,
          creditLeft: currentCreditLeft + orderCost,
        })
      } else {
        message.error("Client document not found")
      }

      message.success(
        `${
          collectionName === "invoices" ? "Invoice" : "Proforma"
        } and related orders marked as paid, and client credit updated`
      )
    } catch (error) {
      message.error(`Error updating ${collectionName}: ${error.message}`)
    }
  }

  // Content to be rendered inside the Popconfirm.
  const popconfirmContent = (
    <div>
      <div>Please select payment date:</div>
      <DatePicker value={selectedDate} onChange={(date) => setSelectedDate(date)} />
    </div>
  )

  // Render based on the current status.
  switch (status) {
    case 0:
      return <Badge status="error" text="No Document" />
    case 1:
      return (
        <div>
          <Popconfirm
            title={popconfirmContent}
            onConfirm={handleMarkPaid}
            okText="Confirm"
            cancelText="Cancel">
            <Button type="primary" size="small">
              Mark as Paid
            </Button>
          </Popconfirm>
        </div>
      )
    case 2:
      return <Badge status="success" text="Paid" />
    default:
      return <Badge status="default" text="ERROR!" />
  }
}
